import { firstValueFrom, Observable, of, ReplaySubject, startWith, switchMap, withLatestFrom } from 'rxjs'
import { Apollo } from 'apollo-angular'
import { catchError, map } from 'rxjs/operators'
import { Injectable } from '@angular/core'

import {
    AddToWishlistMutation,
    AddToWishlistMutationService,
    GetWishlistQueryService,
    RemoveFromWishlistMutation,
    RemoveFromWishlistMutationService,
    SimpleProductFragment,
} from '@app-graphql/schema'
import { AuthService } from '@app-services'

@Injectable({
    providedIn: 'root',
})
export class WishlistService {
    private refreshWishlistSubject = new ReplaySubject<SimpleProductFragment[]>()

    public wishlist$: Observable<SimpleProductFragment[]> = this.refreshWishlistSubject.asObservable().pipe(
        startWith(undefined),
        withLatestFrom(this.authService.user$),
        switchMap(([, user]) => user ? this.getWishlist() : of([])),
    )

    constructor(
        private addToWishlistMutationService: AddToWishlistMutationService,
        private removeFromWishlistMutationService: RemoveFromWishlistMutationService,
        private getWishlistQueryService: GetWishlistQueryService,
        private authService: AuthService,
    ) {
    }

    public addToWishlist(eanCodes: string[]): Observable<Apollo.MutateResult<AddToWishlistMutation>> {
        return this.addToWishlistMutationService.mutate({
            variables: {
                eanCodes,
            },
        })
    }

    public removeFromWishlist(eanCodes: string[]): Observable<Apollo.MutateResult<RemoveFromWishlistMutation>> {
        return this.removeFromWishlistMutationService.mutate({
            variables: {
                eanCodes,
            },
        })
    }

    public async refreshWishlist(): Promise<void> {
        const i = await firstValueFrom(this.getWishlist())
        this.refreshWishlistSubject.next(i)
    }

    private getWishlist(): Observable<SimpleProductFragment[]> {
        return this.getWishlistQueryService.fetch({
            fetchPolicy: 'no-cache',
        }).pipe(
            catchError(() => []),
            map((result) => {
                return (result.data?.me?.wishListItems.map((x) => x.product) ?? []) as SimpleProductFragment[]
            }),
        )
    }
}
